import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ArrowUpRight, Loader2 } from 'lucide-react';
import { sectionMeta } from '../lib/section-meta';
import { accent, springs } from '../lib/utils-app';
import api from '../lib/api';
import SolveItLink from '../components/SolveItLink';
import { SOLVEIT_JEE_MAIN_MATHS } from '../lib/solveit';

export default function SectionPage() {
  const { subjectId, sectionId } = useParams();
  const meta = sectionMeta(sectionId, subjectId);
  const Icon = meta.icon;
  const a = accent(meta.accent);

  const [topics, setTopics] = useState(null);
  const [sectionInfo, setSectionInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([api.listTopics(sectionId, subjectId), api.listSections(subjectId)])
      .then(([ts, sections]) => {
        if (!alive) return;
        setTopics(ts);
        setSectionInfo(sections.find((s) => s.id === sectionId));
      })
      .catch((err) => alive && setError(err.message || 'Failed to load'));
    return () => { alive = false; };
  }, [subjectId, sectionId]);

  const sectionName = sectionInfo?.name || sectionId.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div>
      <Link to={`/subject/${subjectId}`} className="inline-flex items-center text-[12.5px] text-slate-500 hover:text-slate-200 transition mb-6">
        <ChevronLeft className="w-3.5 h-3.5 mr-0.5" /> Library
      </Link>

      <motion.header
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={springs.slow}
        className="relative overflow-hidden rounded-2xl glass hairline px-7 py-8 mb-8"
      >
        <div className={`absolute -right-16 -top-16 w-64 h-64 rounded-full bg-gradient-to-br ${a.grad} blur-2xl opacity-90`} />
        <div className="relative flex items-center gap-5">
          <div className={`w-14 h-14 rounded-2xl ${a.soft} ring-1 ${a.ring} flex items-center justify-center ${a.text}`}>
            <Icon className="w-7 h-7" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Volume · Section</div>
            <h1 className="font-serif-q text-[34px] tracking-tight text-slate-50 leading-tight">{sectionName}</h1>
            <p className="text-[13.5px] text-slate-400 mt-1">{meta.blurb}</p>
            {subjectId === 'math' && (
              <SolveItLink lead="Also preparing for JEE Main?" href={SOLVEIT_JEE_MAIN_MATHS.href}>
                Chapter-wise JEE Main Maths PYQs on SolveIT
              </SolveItLink>
            )}
          </div>
          <div className="ml-auto text-right shrink-0">
            <div className="font-serif-q text-3xl text-slate-100">{sectionInfo?.count ?? '—'}</div>
            <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500">questions</div>
          </div>
        </div>
      </motion.header>

      {error && <div className="rounded-xl glass hairline p-6 text-rose-300">Failed to load topics: {error}</div>}
      {!topics && !error && (
        <div className="rounded-2xl glass hairline p-12 flex items-center justify-center text-slate-400 gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading chapters…
        </div>
      )}
      {topics && topics.length === 0 && (
        <div className="text-center py-16 text-slate-500 rounded-2xl glass hairline">No chapters in this section yet.</div>
      )}
      {topics && topics.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {topics.map((t, i) => (
            <motion.div key={t.id}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ ...springs.soft, delay: i * 0.025 }}>
              <Link to={`/subject/${subjectId}/section/${sectionId}/topic/${t.id}`}
                className="group flex items-center justify-between rounded-xl glass hairline px-4 py-4 hover:bg-white/[0.04] transition">
                <div className="min-w-0">
                  <div className="font-serif-q text-[16.5px] text-slate-100 truncate group-hover:text-indigo-200">{t.name}</div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mt-0.5">{t.count} questions</div>
                </div>
                <ArrowUpRight className={`w-4 h-4 ${a.text} opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition`} />
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
